import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, LabelList
} from 'recharts';
import {
  ChevronRight, Filter, FileText, Flag, Check, X, Minus,
  Calendar, Hash, Activity, Layers, ArrowRight
} from 'lucide-react';
import config from '../../config';
import './AnalisisMente.css';

const RM2025_SESSION_ID = 9;

// Premium palette — calibrated for medical analytics (not Tailwind default)
const PALETTE = {
  success: { fill: '#10b981', text: '#059669', textDark: '#34d399' },
  danger:  { fill: '#f43f5e', text: '#e11d48', textDark: '#fb7185' },  // rose, not red
  warning: { fill: '#f59e0b', text: '#d97706', textDark: '#fbbf24' },
  info:    { fill: '#6366f1', text: '#4f46e5', textDark: '#818cf8' },  // indigo, not blue
};

const STATUS_META = {
  correct:   { label: 'Correcta',   dot: PALETTE.success.fill, text: 'text-emerald-500', Icon: Check },
  incorrect: { label: 'Incorrecta', dot: PALETTE.danger.fill,  text: 'text-rose-500',    Icon: X },
  skipped:   { label: 'Omitida',    dot: PALETTE.warning.fill, text: 'text-amber-500',   Icon: Minus },
};

const accuracyColor = (acc) => {
  const n = parseFloat(acc) || 0;
  if (n >= 70) return PALETTE.success.fill;
  if (n >= 50) return PALETTE.info.fill;
  if (n >= 30) return PALETTE.warning.fill;
  return PALETTE.danger.fill;
};

const accuracyTextColor = (acc, darkMode) => {
  const n = parseFloat(acc) || 0;
  if (n >= 70) return darkMode ? 'text-emerald-400' : 'text-emerald-600';
  if (n >= 50) return darkMode ? 'text-indigo-400' : 'text-indigo-600';
  if (n >= 30) return darkMode ? 'text-amber-400' : 'text-amber-600';
  return darkMode ? 'text-rose-400' : 'text-rose-600';
};

export default function RM2025View({ darkMode = false }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [view, setView] = useState('overview');
  const [selectedArea, setSelectedArea] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  const cardClass = darkMode ? 'am-card-dark' : 'am-card-light';
  const text = darkMode ? 'text-slate-100' : 'text-slate-900';
  const subText = darkMode ? 'text-slate-400' : 'text-slate-500';
  const muted = darkMode ? 'text-slate-500' : 'text-slate-400';
  const innerBg = darkMode ? 'bg-slate-900/40' : 'bg-slate-50/80';
  const hairline = darkMode ? 'am-hairline-dark' : 'am-hairline-light';

  useEffect(() => {
    const fetchSession = async () => {
      setLoading(true);
      setError(null);
      try {
        const apiUrl = config.getApiUrl();
        const res = await fetch(`${apiUrl}/exam-sessions/${RM2025_SESSION_ID}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setSession(data.session);
        setAnswers(data.answers || []);
      } catch (e) {
        console.error('Error cargando RM 2025:', e);
        setError(e.message || 'Error al cargar datos');
      } finally {
        setLoading(false);
      }
    };
    fetchSession();
  }, []);

  const stats = useMemo(() => {
    if (!answers.length) return null;
    const correct = answers.filter(a => a.status === 'correct').length;
    const incorrect = answers.filter(a => a.status === 'incorrect').length;
    const skipped = answers.filter(a => a.status === 'skipped').length;
    const total = answers.length;
    const answered = correct + incorrect;
    const accuracyTotal = total > 0 ? ((correct / total) * 100).toFixed(1) : 0;
    const accuracyAnswered = answered > 0 ? ((correct / answered) * 100).toFixed(1) : 0;
    const withNotes = answers.filter(a => a.note && a.note.trim()).length;
    const flagged = answers.filter(a => a.flagged).length;
    return { correct, incorrect, skipped, total, answered, accuracyTotal, accuracyAnswered, withNotes, flagged };
  }, [answers]);

  const byArea = useMemo(() => {
    if (!answers.length) return [];
    const map = {};
    for (const a of answers) {
      const area = a.area || 'Sin área';
      if (!map[area]) map[area] = { area, total: 0, correct: 0, incorrect: 0, skipped: 0 };
      map[area].total += 1;
      if (a.status === 'correct') map[area].correct += 1;
      else if (a.status === 'incorrect') map[area].incorrect += 1;
      else if (a.status === 'skipped') map[area].skipped += 1;
    }
    return Object.values(map)
      .map(r => ({
        ...r,
        accuracy: r.total > 0 ? parseFloat(((r.correct / r.total) * 100).toFixed(1)) : 0,
        shortName: r.area.length > 12 ? r.area.substring(0, 11) + '…' : r.area,
      }))
      .sort((a, b) => b.total - a.total);
  }, [answers]);

  const topicsForSelectedArea = useMemo(() => {
    if (!selectedArea) return [];
    const filtered = answers.filter(a => a.area === selectedArea);
    const map = {};
    for (const a of filtered) {
      const topic = a.topic || 'Sin tema';
      if (!map[topic]) map[topic] = { topic, total: 0, correct: 0, incorrect: 0, skipped: 0 };
      map[topic].total += 1;
      if (a.status === 'correct') map[topic].correct += 1;
      else if (a.status === 'incorrect') map[topic].incorrect += 1;
      else if (a.status === 'skipped') map[topic].skipped += 1;
    }
    return Object.values(map)
      .map(r => ({
        ...r,
        accuracy: r.total > 0 ? parseFloat(((r.correct / r.total) * 100).toFixed(1)) : 0,
      }))
      .sort((a, b) => b.total - a.total);
  }, [answers, selectedArea]);

  const questionsForSelectedTopic = useMemo(() => {
    if (!selectedArea || !selectedTopic) return [];
    let filtered = answers.filter(a => a.area === selectedArea && a.topic === selectedTopic);
    if (filterStatus !== 'all') {
      filtered = filtered.filter(a => a.status === filterStatus);
    }
    return filtered.sort((a, b) => a.question_index - b.question_index);
  }, [answers, selectedArea, selectedTopic, filterStatus]);

  const goToArea = (area) => {
    setSelectedArea(area);
    setSelectedTopic(null);
    setSelectedQuestion(null);
    setFilterStatus('all');
    setView('area');
  };
  const goToTopic = (topic) => {
    setSelectedTopic(topic);
    setSelectedQuestion(null);
    setFilterStatus('all');
    setView('topic');
  };
  const goToQuestion = (q) => {
    setSelectedQuestion(q);
    setView('question');
  };

  if (loading) {
    return (
      <div className={`${cardClass} rounded-2xl p-16 text-center am-noise`}>
        <motion.div
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className={`am-mono text-sm tracking-wider ${muted}`}
        >
          CARGANDO BASELINE · MYSQL://session/9
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${cardClass} rounded-2xl p-10`}>
        <div className={`am-eyebrow mb-3 text-rose-500`}>ERROR DE CONEXIÓN</div>
        <p className={`am-serif text-2xl mb-2 ${text}`}>{error}</p>
        <p className={`am-mono text-xs ${muted}`}>
          Verificar server local en localhost:3001 · Session #{RM2025_SESSION_ID}
        </p>
      </div>
    );
  }

  // ===========================================================================
  // BREADCRUMB — refined
  // ===========================================================================
  const Breadcrumb = () => (
    <div className={`flex items-center gap-2 mb-8 am-mono text-xs`}>
      <button
        onClick={() => { setView('overview'); setSelectedArea(null); setSelectedTopic(null); setSelectedQuestion(null); }}
        className={`am-focus uppercase tracking-widest hover:underline transition-colors ${
          view === 'overview' ? text + ' font-bold' : subText + ' hover:' + (darkMode ? 'text-slate-300' : 'text-slate-700')
        }`}
      >
        RM 2025
      </button>
      {selectedArea && (
        <>
          <ChevronRight className={`w-3 h-3 ${muted}`} />
          <button
            onClick={() => { setView('area'); setSelectedTopic(null); setSelectedQuestion(null); }}
            className={`am-focus uppercase tracking-widest hover:underline truncate max-w-[200px] ${
              view === 'area' ? text + ' font-bold' : subText
            }`}
          >
            {selectedArea}
          </button>
        </>
      )}
      {selectedTopic && (
        <>
          <ChevronRight className={`w-3 h-3 ${muted}`} />
          <button
            onClick={() => { setView('topic'); setSelectedQuestion(null); }}
            className={`am-focus uppercase tracking-widest hover:underline truncate max-w-[200px] ${
              view === 'topic' ? text + ' font-bold' : subText
            }`}
          >
            {selectedTopic}
          </button>
        </>
      )}
      {selectedQuestion && view === 'question' && (
        <>
          <ChevronRight className={`w-3 h-3 ${muted}`} />
          <span className={`uppercase tracking-widest font-bold ${text}`}>
            #{(selectedQuestion.question_index ?? 0) + 1}
          </span>
        </>
      )}
    </div>
  );

  // ===========================================================================
  // TOOLTIP premium para Recharts
  // ===========================================================================
  const ChartTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
      <div className={`${cardClass} rounded-xl p-4 min-w-[200px]`} style={{ boxShadow: '0 24px 60px -12px rgba(0,0,0,0.5)' }}>
        <div className={`am-eyebrow ${muted} mb-2`}>{d.area}</div>
        <div className="flex items-baseline gap-2 mb-3">
          <span className={`am-display text-4xl font-medium`} style={{ color: accuracyColor(d.accuracy) }}>
            {d.accuracy}
          </span>
          <span className={`am-mono text-sm ${subText}`}>%</span>
        </div>
        <div className={`${hairline} mb-3`} />
        <div className={`grid grid-cols-3 gap-3 am-mono text-xs`}>
          <div>
            <div className={`${muted} text-[10px] uppercase tracking-wider mb-1`}>OK</div>
            <div className="text-emerald-500 font-semibold">{d.correct}</div>
          </div>
          <div>
            <div className={`${muted} text-[10px] uppercase tracking-wider mb-1`}>FAIL</div>
            <div className="text-rose-500 font-semibold">{d.incorrect}</div>
          </div>
          <div>
            <div className={`${muted} text-[10px] uppercase tracking-wider mb-1`}>SKIP</div>
            <div className="text-amber-500 font-semibold">{d.skipped}</div>
          </div>
        </div>
      </div>
    );
  };

  // ===========================================================================
  // VISTA OVERVIEW
  // ===========================================================================
  if (view === 'overview') {
    return (
      <div>
        <Breadcrumb />

        {/* HERO editorial */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="grid lg:grid-cols-12 gap-8 mb-14"
        >
          <div className="lg:col-span-7">
            <div className={`am-eyebrow mb-4 ${muted}`}>
              SESSION#{RM2025_SESSION_ID} · BASELINE · {session?.completed_at ? new Date(session.completed_at).toLocaleDateString('es-PE', { year: 'numeric', month: 'long', day: 'numeric' }).toUpperCase() : '—'}
            </div>
            <div className="flex items-baseline gap-4 flex-wrap mb-4">
              <h2
                className="am-display am-tabular text-[140px] md:text-[180px] font-medium leading-none"
                style={{ color: accuracyColor(stats?.accuracyTotal) }}
              >
                {stats?.accuracyTotal}
              </h2>
              <div className="flex flex-col gap-1">
                <span className={`am-display text-5xl font-light ${subText}`}>%</span>
                <span className={`am-mono text-xs uppercase tracking-widest ${muted}`}>accuracy</span>
              </div>
            </div>
            <p className={`am-serif italic text-lg max-w-md font-light ${subText} leading-snug`}>
              de las {stats?.total} preguntas tomadas en el RM 2025, {stats?.correct} fueron correctas — {stats?.accuracyAnswered}% sobre las que respondiste.
            </p>
          </div>

          <div className="lg:col-span-5 grid grid-cols-2 gap-3">
            <StatTile label="Correctas" value={stats?.correct} accent="#10b981" darkMode={darkMode} delay={0.1} />
            <StatTile label="Incorrectas" value={stats?.incorrect} accent={PALETTE.danger.fill} darkMode={darkMode} delay={0.15} />
            <StatTile label="Omitidas" value={stats?.skipped} accent="#f59e0b" darkMode={darkMode} delay={0.2} />
            <StatTile label="Con notas" value={stats?.withNotes} accent="#6366f1" sub={`${stats?.flagged} flagged`} darkMode={darkMode} delay={0.25} />
          </div>
        </motion.section>

        <div className={`${hairline} mb-12`} />

        {/* GRÁFICO VERTICAL — la estrella */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className={`${cardClass} am-noise rounded-3xl p-8 mb-10`}
        >
          <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
            <div>
              <div className={`am-eyebrow mb-2 ${muted}`}>FIG. 01 · DISTRIBUCIÓN</div>
              <h3 className={`am-display text-3xl font-medium ${text}`}>
                Performance <span className="italic font-light">por área</span>
              </h3>
              <p className={`am-sans text-sm mt-2 ${subText}`}>
                {byArea.length} áreas evaluadas · click sobre cualquier barra para profundizar
              </p>
            </div>
            <div className={`am-mono text-xs flex gap-4 ${subText}`}>
              <Legend color="#10b981" label="≥70%" />
              <Legend color={PALETTE.info.fill} label="50–70%" />
              <Legend color="#f59e0b" label="30–50%" />
              <Legend color={PALETTE.danger.fill} label="<30%" />
            </div>
          </div>

          <div style={{ width: '100%', height: 420 }}>
            <ResponsiveContainer>
              <BarChart
                data={byArea}
                margin={{ top: 30, right: 0, bottom: 70, left: 0 }}
                onClick={(e) => e?.activePayload?.[0]?.payload && goToArea(e.activePayload[0].payload.area)}
              >
                <defs>
                  {byArea.map((a, i) => {
                    const c = accuracyColor(a.accuracy);
                    return (
                      <linearGradient key={a.area} id={`grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={c} stopOpacity={0.95} />
                        <stop offset="100%" stopColor={c} stopOpacity={0.4} />
                      </linearGradient>
                    );
                  })}
                </defs>
                <CartesianGrid
                  strokeDasharray="2 4"
                  stroke={darkMode ? 'rgba(148,163,184,0.1)' : 'rgba(15,23,42,0.06)'}
                  vertical={false}
                />
                <XAxis
                  dataKey="shortName"
                  stroke={darkMode ? '#64748b' : '#94a3b8'}
                  tick={{ fontSize: 11 }}
                  angle={-35}
                  textAnchor="end"
                  height={70}
                  axisLine={{ stroke: darkMode ? 'rgba(148,163,184,0.15)' : 'rgba(15,23,42,0.1)' }}
                  tickLine={false}
                />
                <YAxis
                  stroke={darkMode ? '#64748b' : '#94a3b8'}
                  tick={{ fontSize: 11 }}
                  domain={[0, 100]}
                  ticks={[0, 25, 50, 75, 100]}
                  tickFormatter={(v) => `${v}%`}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: darkMode ? 'rgba(148,163,184,0.05)' : 'rgba(15,23,42,0.03)' }} />
                <Bar dataKey="accuracy" radius={[6, 6, 0, 0]} cursor="pointer">
                  {byArea.map((a, i) => (
                    <Cell key={a.area} fill={`url(#grad-${i})`} />
                  ))}
                  <LabelList
                    dataKey="accuracy"
                    position="top"
                    formatter={(v) => `${v}%`}
                    style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, fill: darkMode ? '#cbd5e1' : '#475569', fontWeight: 600 }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.section>

        {/* GRID de cards de área — Bloomberg-style mini-dashboards */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
            <div>
              <div className={`am-eyebrow mb-2 ${muted}`}>GRID · ÁREAS</div>
              <h3 className={`am-display text-3xl font-medium ${text}`}>
                Explorar <span className="italic font-light">por área</span>
              </h3>
              <p className={`am-sans text-sm mt-2 ${subText}`}>
                {byArea.length} áreas · click sobre cualquier card para profundizar
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {byArea.map((a, i) => (
              <MetricCard
                key={a.area}
                item={{ ...a, name: a.area }}
                index={i}
                darkMode={darkMode}
                onClick={() => goToArea(a.area)}
                ctaLabel="ver temas"
              />
            ))}
          </div>
        </motion.section>
      </div>
    );
  }

  // ===========================================================================
  // VISTA AREA — topics
  // ===========================================================================
  if (view === 'area') {
    const areaStats = byArea.find(a => a.area === selectedArea);
    return (
      <div>
        <Breadcrumb />

        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className={`${cardClass} am-noise rounded-3xl p-8 mb-10`}
        >
          <div className={`am-eyebrow mb-3 ${muted}`}>ÁREA · {areaStats?.total} PREGUNTAS</div>
          <div className="flex items-end gap-6 flex-wrap">
            <h2 className={`am-display text-5xl font-medium ${text}`}>
              {selectedArea}
            </h2>
            <div className="flex items-baseline gap-1">
              <span
                className={`am-numeric-xl text-7xl`}
                style={{ color: accuracyColor(areaStats?.accuracy) }}
              >
                {areaStats?.accuracy}
              </span>
              <span className={`am-mono text-base ${subText}`}>%</span>
            </div>
          </div>
          <div className={`am-mono text-xs mt-4 flex gap-4 ${subText}`}>
            <span className="text-emerald-500">{areaStats?.correct} correctas</span>
            <span className="text-rose-500">{areaStats?.incorrect} incorrectas</span>
            <span className="text-amber-500">{areaStats?.skipped} omitidas</span>
          </div>
        </motion.section>

        {/* Vertical bar chart de topics */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className={`${cardClass} rounded-3xl p-8 mb-8`}
        >
          <div className="mb-6">
            <div className={`am-eyebrow mb-2 ${muted}`}>FIG. 02 · TEMAS DEL ÁREA</div>
            <h3 className={`am-display text-2xl font-medium ${text}`}>
              {topicsForSelectedArea.length} <span className="italic font-light">temas</span>
            </h3>
          </div>

          <div style={{ width: '100%', height: 360 }}>
            <ResponsiveContainer>
              <BarChart
                data={topicsForSelectedArea.map(t => ({ ...t, shortName: t.topic.length > 16 ? t.topic.substring(0, 15) + '…' : t.topic }))}
                margin={{ top: 24, right: 0, bottom: 80, left: 0 }}
                onClick={(e) => e?.activePayload?.[0]?.payload && goToTopic(e.activePayload[0].payload.topic)}
              >
                <defs>
                  {topicsForSelectedArea.map((t, i) => {
                    const c = accuracyColor(t.accuracy);
                    return (
                      <linearGradient key={t.topic} id={`tgrad-${i}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={c} stopOpacity={0.95} />
                        <stop offset="100%" stopColor={c} stopOpacity={0.4} />
                      </linearGradient>
                    );
                  })}
                </defs>
                <CartesianGrid strokeDasharray="2 4" stroke={darkMode ? 'rgba(148,163,184,0.1)' : 'rgba(15,23,42,0.06)'} vertical={false} />
                <XAxis dataKey="shortName" stroke={darkMode ? '#64748b' : '#94a3b8'} tick={{ fontSize: 10 }} angle={-40} textAnchor="end" height={80} axisLine={{ stroke: darkMode ? 'rgba(148,163,184,0.15)' : 'rgba(15,23,42,0.1)' }} tickLine={false} />
                <YAxis stroke={darkMode ? '#64748b' : '#94a3b8'} tick={{ fontSize: 10 }} domain={[0, 100]} ticks={[0, 50, 100]} tickFormatter={(v) => `${v}%`} axisLine={false} tickLine={false} />
                <Tooltip
                  cursor={{ fill: darkMode ? 'rgba(148,163,184,0.05)' : 'rgba(15,23,42,0.03)' }}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload;
                    return (
                      <div className={`${cardClass} rounded-xl p-3 min-w-[180px]`}>
                        <div className={`am-eyebrow mb-1 ${muted}`}>{d.topic}</div>
                        <div className={`am-display text-3xl`} style={{ color: accuracyColor(d.accuracy) }}>{d.accuracy}<span className={`am-mono text-sm ml-0.5 ${subText}`}>%</span></div>
                        <div className={`am-mono text-[10px] mt-1 ${subText}`}>{d.correct}/{d.total} correctas</div>
                      </div>
                    );
                  }}
                />
                <Bar dataKey="accuracy" radius={[6, 6, 0, 0]} cursor="pointer">
                  {topicsForSelectedArea.map((t, i) => (
                    <Cell key={t.topic} fill={`url(#tgrad-${i})`} />
                  ))}
                  <LabelList dataKey="accuracy" position="top" formatter={(v) => `${v}%`} style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, fill: darkMode ? '#cbd5e1' : '#475569', fontWeight: 600 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.section>

        {/* Grid premium de topics */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
            <div>
              <div className={`am-eyebrow mb-2 ${muted}`}>GRID · TEMAS</div>
              <h3 className={`am-display text-3xl font-medium ${text}`}>
                Explorar <span className="italic font-light">por tema</span>
              </h3>
              <p className={`am-sans text-sm mt-2 ${subText}`}>
                {topicsForSelectedArea.length} temas · click para ver preguntas
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {topicsForSelectedArea.map((t, i) => (
              <MetricCard
                key={t.topic}
                item={{ ...t, name: t.topic }}
                index={i}
                darkMode={darkMode}
                onClick={() => goToTopic(t.topic)}
                ctaLabel="ver preguntas"
              />
            ))}
          </div>
        </motion.section>
      </div>
    );
  }

  // ===========================================================================
  // VISTA TOPIC — preguntas
  // ===========================================================================
  if (view === 'topic') {
    const topicStats = topicsForSelectedArea.find(t => t.topic === selectedTopic);
    return (
      <div>
        <Breadcrumb />

        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className={`${cardClass} am-noise rounded-3xl p-8 mb-8`}
        >
          <div className={`am-eyebrow mb-3 ${muted}`}>TEMA · {selectedArea}</div>
          <div className="flex items-end gap-6 flex-wrap">
            <h2 className={`am-display text-4xl font-medium ${text}`}>{selectedTopic}</h2>
            <div className="flex items-baseline gap-1">
              <span className={`am-numeric-xl text-5xl`} style={{ color: accuracyColor(topicStats?.accuracy) }}>
                {topicStats?.accuracy}
              </span>
              <span className={`am-mono text-sm ${subText}`}>%</span>
            </div>
          </div>
        </motion.section>

        {/* Filtros refinados */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className={`flex items-center gap-2 mb-6 flex-wrap`}
        >
          <Filter className={`w-3.5 h-3.5 ${muted}`} />
          <span className={`am-eyebrow ${muted} mr-2`}>FILTRAR</span>
          {[
            { id: 'all', label: 'Todas', count: topicStats?.total, color: null },
            { id: 'correct', label: 'Correctas', count: topicStats?.correct, color: '#10b981' },
            { id: 'incorrect', label: 'Incorrectas', count: topicStats?.incorrect, color: PALETTE.danger.fill },
            { id: 'skipped', label: 'Omitidas', count: topicStats?.skipped, color: '#f59e0b' },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFilterStatus(f.id)}
              className={`am-focus flex items-center gap-2 px-3 py-1.5 rounded-full am-mono text-xs transition-all ${
                filterStatus === f.id
                  ? darkMode ? 'bg-slate-100 text-slate-900' : 'bg-slate-900 text-white'
                  : darkMode ? 'bg-slate-800/60 text-slate-300 hover:bg-slate-800' : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              {f.color && <span className="am-dot" style={{ background: f.color }} />}
              <span className="font-semibold">{f.label}</span>
              <span className="opacity-60">{f.count || 0}</span>
            </button>
          ))}
        </motion.div>

        <div className="space-y-3">
          {questionsForSelectedTopic.length === 0 && (
            <div className={`${cardClass} rounded-2xl p-10 text-center`}>
              <p className={`am-serif italic ${subText}`}>No hay preguntas con este filtro.</p>
            </div>
          )}
          {questionsForSelectedTopic.map((q, i) => {
            const meta = STATUS_META[q.status];
            return (
              <motion.button
                key={q.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.04 * i }}
                onClick={() => goToQuestion(q)}
                className={`am-focus am-lift w-full text-left ${cardClass} rounded-2xl p-5 group`}
              >
                <div className="flex items-start gap-4">
                  <div className={`am-mono am-tabular text-xs px-2 py-1 rounded-md ${darkMode ? 'bg-slate-800' : 'bg-slate-100'} ${subText} font-semibold flex-shrink-0`}>
                    #{(q.question_index ?? 0) + 1}
                  </div>

                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md am-mono text-[10px] uppercase tracking-wider font-bold ${
                    darkMode ? 'bg-slate-800/80' : 'bg-slate-100'
                  } ${meta?.text} flex-shrink-0`}>
                    <span className="am-dot" style={{ background: meta?.dot }} />
                    {meta?.label}
                  </div>

                  {q.confidence ? (
                    <div className={`am-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-md ${darkMode ? 'bg-slate-800/60 text-slate-400' : 'bg-slate-50 text-slate-500'} flex-shrink-0`}>
                      conf {q.confidence}/3
                    </div>
                  ) : null}

                  {q.flagged ? <Flag className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-1" fill="currentColor" /> : null}
                  {q.note && q.note.trim() ? <FileText className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0 mt-1" /> : null}

                  <div className={`am-mono text-[10px] uppercase tracking-wider ml-auto truncate max-w-[200px] ${muted} flex-shrink-0 mt-1`}>
                    {q.subtopic || ''}
                  </div>
                </div>

                <div className={`am-serif text-base mt-3 line-clamp-2 ${text}`}>
                  {q.question_text || '(sin texto)'}
                </div>

                <div className={`flex items-center justify-end mt-2 ${muted} text-xs am-mono group-hover:gap-2 transition-all`}>
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity uppercase tracking-widest">ver detalle</span>
                  <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    );
  }

  // ===========================================================================
  // VISTA QUESTION — detalle premium
  // ===========================================================================
  if (view === 'question' && selectedQuestion) {
    const q = selectedQuestion;
    const meta = STATUS_META[q.status];
    const isCorrect = q.status === 'correct';
    const isSkipped = q.status === 'skipped';

    return (
      <div>
        <Breadcrumb />

        <motion.article
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className={`${cardClass} am-noise rounded-3xl overflow-hidden`}
        >
          {/* Header de la pregunta */}
          <header className="p-8 pb-6">
            <div className="flex items-start justify-between gap-6 flex-wrap mb-6">
              <div>
                <div className={`am-eyebrow mb-3 ${muted} flex items-center gap-3 flex-wrap`}>
                  <span className="flex items-center gap-1.5"><Hash className="w-3 h-3" /> PREGUNTA {(q.question_index ?? 0) + 1} / 200</span>
                  <span>·</span>
                  <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {q.year}</span>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full am-mono text-xs uppercase tracking-wider font-bold ${meta?.text} ${
                    darkMode ? 'bg-slate-800/80' : 'bg-slate-100'
                  }`}>
                    <span className="am-dot" style={{ background: meta?.dot }} />
                    {meta?.label}
                  </div>
                  {q.confidence ? (
                    <div className={`am-mono text-xs uppercase tracking-wider px-3 py-1.5 rounded-full ${darkMode ? 'bg-slate-800/60 text-slate-400' : 'bg-slate-50 text-slate-500 border border-slate-200'}`}>
                      Confianza · {q.confidence}/3
                    </div>
                  ) : null}
                  {q.flagged ? (
                    <div className={`flex items-center gap-1.5 am-mono text-xs uppercase tracking-wider px-3 py-1.5 rounded-full bg-amber-500/10 text-amber-500`}>
                      <Flag className="w-3 h-3" fill="currentColor" /> flagged
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className={`flex flex-wrap gap-4 am-mono text-[10px] uppercase tracking-widest ${muted} pb-2`}>
              <span><Layers className="w-3 h-3 inline mr-1" />{q.area}</span>
              <span>›</span>
              <span>{q.topic}</span>
              {q.subtopic && <><span>›</span><span>{q.subtopic}</span></>}
              {q.subtopic_specific && <><span>›</span><span className={text + ' font-bold'}>{q.subtopic_specific}</span></>}
            </div>
          </header>

          <div className={`${hairline} mx-8`} />

          <div className="p-8 space-y-6">
            {q.case_description && (
              <div className={`${innerBg} rounded-2xl p-6 border ${darkMode ? 'border-slate-700/50' : 'border-slate-200/50'}`}>
                <div className={`am-eyebrow mb-3 ${muted}`}>CASO CLÍNICO</div>
                <p className={`am-serif text-[15px] leading-relaxed whitespace-pre-wrap ${text}`}>
                  {q.case_description}
                </p>
              </div>
            )}

            <div>
              <div className={`am-eyebrow mb-3 ${muted}`}>PREGUNTA</div>
              <p className={`am-display text-2xl font-medium leading-tight ${text}`}>
                {q.question_text}
              </p>
            </div>

            {/* Tu respuesta vs Correcta */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className={`rounded-2xl p-5 border ${
                isSkipped
                  ? (darkMode ? 'border-amber-500/40 bg-amber-500/5' : 'border-amber-300 bg-amber-50/50')
                  : isCorrect
                    ? (darkMode ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-emerald-300 bg-emerald-50/50')
                    : (darkMode ? 'border-rose-500/40 bg-rose-500/5' : 'border-rose-300 bg-rose-50/50')
              }`}>
                <div className={`am-eyebrow mb-2 ${
                  isSkipped ? 'text-amber-500' : isCorrect ? 'text-emerald-500' : 'text-rose-500'
                }`}>TU RESPUESTA</div>
                <div className={`am-serif text-lg ${text}`}>
                  {q.user_answer_label ? (
                    <>
                      <span className="am-mono text-2xl font-bold mr-2 opacity-60">{q.user_answer_label}.</span>
                      {q.user_answer_text}
                    </>
                  ) : (
                    <span className="italic opacity-60">— omitida —</span>
                  )}
                </div>
              </div>

              <div className={`rounded-2xl p-5 border ${
                darkMode ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-emerald-300 bg-emerald-50/50'
              }`}>
                <div className={`am-eyebrow mb-2 text-emerald-500`}>CORRECTA</div>
                <div className={`am-serif text-lg ${text}`}>
                  <span className="am-mono text-2xl font-bold mr-2 text-emerald-500">{q.correct_answer_label}.</span>
                  {q.correct_answer_text}
                </div>
              </div>
            </div>

            {/* Tu nota */}
            {q.note && q.note.trim() && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className={`relative rounded-2xl p-6 border ${darkMode ? 'border-indigo-500/30 bg-indigo-500/5' : 'border-indigo-200 bg-indigo-50/40'}`}
              >
                <div className={`am-eyebrow mb-3 flex items-center gap-2 ${darkMode ? 'text-indigo-300' : 'text-indigo-600'}`}>
                  <FileText className="w-3 h-3" /> TU NOTA · ESCENA-EXAMEN
                </div>
                <p className={`am-serif italic text-[15px] leading-relaxed whitespace-pre-wrap ${text}`}>
                  "{q.note}"
                </p>
              </motion.div>
            )}

            {q.explanation && (
              <div className={`${innerBg} rounded-2xl p-6 border ${darkMode ? 'border-slate-700/50' : 'border-slate-200/50'}`}>
                <div className={`am-eyebrow mb-3 ${muted}`}>EXPLICACIÓN</div>
                <p className={`am-serif text-[14px] leading-relaxed whitespace-pre-wrap ${text}`}>
                  {q.explanation}
                </p>
              </div>
            )}
          </div>
        </motion.article>
      </div>
    );
  }

  return null;
}

// ============================================================================
// SUBCOMPONENTES
// ============================================================================

function StatTile({ label, value, accent, sub, darkMode, delay = 0 }) {
  const cardClass = darkMode ? 'am-card-dark' : 'am-card-light';
  const text = darkMode ? 'text-slate-100' : 'text-slate-900';
  const muted = darkMode ? 'text-slate-500' : 'text-slate-400';
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay, ease: [0.2, 0.8, 0.2, 1] }}
      className={`${cardClass} am-noise rounded-2xl p-4 relative overflow-hidden`}
    >
      <div className="absolute top-0 left-0 w-full h-1" style={{ background: accent }} />
      <div className={`am-eyebrow mb-2 ${muted}`}>{label}</div>
      <div className={`am-numeric text-4xl ${text}`}>
        {value ?? '—'}
      </div>
      {sub && <div className={`am-mono text-[10px] uppercase tracking-widest mt-2 ${muted}`}>{sub}</div>}
    </motion.div>
  );
}

function Legend({ color, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="am-dot" style={{ background: color }} />
      <span>{label}</span>
    </div>
  );
}

// ============================================================================
// MetricCard — mini-dashboard premium reusable (áreas y temas)
// ============================================================================
function MetricCard({ item, index, darkMode, onClick, ctaLabel = 'explorar' }) {
  const cardClass = darkMode ? 'am-card-dark' : 'am-card-light';
  const text = darkMode ? 'text-slate-100' : 'text-slate-900';
  const subText = darkMode ? 'text-slate-400' : 'text-slate-500';
  const muted = darkMode ? 'text-slate-500' : 'text-slate-400';

  const accuracy = parseFloat(item.accuracy) || 0;

  const accent = (() => {
    if (accuracy >= 70) return PALETTE.success.fill;
    if (accuracy >= 50) return PALETTE.info.fill;
    if (accuracy >= 30) return PALETTE.warning.fill;
    return PALETTE.danger.fill;
  })();

  const accentText = (() => {
    if (accuracy >= 70) return darkMode ? 'text-emerald-400' : 'text-emerald-600';
    if (accuracy >= 50) return darkMode ? 'text-indigo-400' : 'text-indigo-600';
    if (accuracy >= 30) return darkMode ? 'text-amber-400' : 'text-amber-600';
    return darkMode ? 'text-rose-400' : 'text-rose-600';
  })();

  const total = item.total || 1;
  const segments = [
    { value: item.correct, color: PALETTE.success.fill },
    { value: item.incorrect, color: PALETTE.danger.fill },
    { value: item.skipped, color: PALETTE.warning.fill },
  ].filter(d => d.value > 0);

  const tier = (() => {
    if (accuracy >= 70) return 'DOMINADO';
    if (accuracy >= 50) return 'EN PROGRESO';
    if (accuracy >= 30) return 'FRÁGIL';
    return 'CRÍTICO';
  })();

  return (
    <motion.button
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.04 * index, ease: [0.2, 0.8, 0.2, 1] }}
      whileHover={{ y: -4 }}
      onClick={onClick}
      className={`am-focus am-noise am-tier-card rounded-2xl p-5 text-left relative overflow-hidden group transition-all duration-300`}
      style={{
        background: darkMode
          ? `
            radial-gradient(ellipse 80% 60% at top right, ${accent}22 0%, transparent 55%),
            radial-gradient(ellipse 100% 80% at bottom left, ${accent}10 0%, transparent 60%),
            linear-gradient(165deg, rgba(30,41,59,0.85) 0%, rgba(15,23,42,0.95) 100%)
          `
          : `
            radial-gradient(ellipse 80% 60% at top right, ${accent}18 0%, transparent 55%),
            radial-gradient(ellipse 100% 80% at bottom left, ${accent}08 0%, transparent 60%),
            linear-gradient(165deg, #ffffff 0%, #fafaf9 100%)
          `,
        border: `1px solid ${accent}${darkMode ? '25' : '20'}`,
        boxShadow: darkMode
          ? `
            inset 0 1px 0 rgba(255,255,255,0.08),
            inset 0 0 0 1px rgba(255,255,255,0.02),
            0 1px 0 rgba(0,0,0,0.3),
            0 8px 24px -8px ${accent}25,
            0 20px 48px -16px rgba(0,0,0,0.5)
          `
          : `
            inset 0 1px 0 rgba(255,255,255,1),
            0 1px 2px rgba(15,23,42,0.04),
            0 8px 24px -8px ${accent}30,
            0 16px 40px -16px rgba(15,23,42,0.15)
          `,
      }}
    >
      {/* Hover glow halo */}
      <div
        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 100% 80% at top right, ${accent}30 0%, transparent 50%)`,
        }}
      />

      {/* Top accent bar — más visible */}
      <div
        className="absolute top-0 left-0 right-0 h-[2px] transition-all"
        style={{
          background: `linear-gradient(90deg, transparent 0%, ${accent} 30%, ${accent} 70%, transparent 100%)`,
          boxShadow: `0 0 12px ${accent}80`,
        }}
      />

      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="flex items-center gap-2">
          <span className={`am-mono text-[11px] font-bold ${muted}`}>
            #{String(index + 1).padStart(2, '0')}
          </span>
          <span
            className="am-eyebrow text-[9px] px-2 py-0.5 rounded-full"
            style={{
              background: `${accent}15`,
              color: accent,
              border: `1px solid ${accent}30`,
            }}
          >
            {tier}
          </span>
        </div>

        {/* Stacked bar — premium financial look */}
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          <div className={`relative flex h-3 w-32 rounded-md overflow-hidden ${
            darkMode ? 'bg-slate-800/70 ring-1 ring-slate-700/50' : 'bg-slate-200/80 ring-1 ring-slate-300/50'
          }`}>
            {segments.map((s, idx) => (
              <motion.div
                key={idx}
                initial={{ width: 0 }}
                animate={{ width: `${(s.value / total) * 100}%` }}
                transition={{ duration: 0.8, delay: 0.04 * index + 0.2 + idx * 0.1, ease: [0.2, 0.8, 0.2, 1] }}
                style={{
                  background: `linear-gradient(180deg, ${s.color} 0%, ${s.color}dd 100%)`,
                  boxShadow: `inset 0 1px 0 rgba(255,255,255,0.15)`,
                }}
                className="h-full"
              />
            ))}
          </div>
          <div className={`am-mono text-[10px] tabular-nums ${muted}`}>
            {item.total} <span className="opacity-60">preguntas</span>
          </div>
        </div>
      </div>

      <div className={`am-serif text-xl font-medium leading-tight mb-3 ${text} line-clamp-2 min-h-[3.5rem]`}>
        {item.name}
      </div>

      <div className="flex items-baseline gap-1 mb-3">
        <span className={`am-numeric-xl text-[64px] ${accentText}`}>
          {item.accuracy}
        </span>
        <span className={`am-mono text-sm font-semibold ${muted}`}>%</span>
      </div>

      <div className={`h-1 rounded-full overflow-hidden mb-4 ${darkMode ? 'bg-slate-800/60' : 'bg-slate-200/60'}`}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${accuracy}%` }}
          transition={{ duration: 0.9, delay: 0.04 * index + 0.3, ease: [0.2, 0.8, 0.2, 1] }}
          className="h-full rounded-full"
          style={{ background: accent }}
        />
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Stat label="OK" value={item.correct} color="text-emerald-500" darkMode={darkMode} />
        <Stat label="FAIL" value={item.incorrect} color="text-rose-500" darkMode={darkMode} />
        <Stat label="SKIP" value={item.skipped} color="text-amber-500" darkMode={darkMode} />
      </div>

      <div className={`flex items-center justify-end mt-4 gap-1 am-mono text-[10px] uppercase tracking-widest ${muted} opacity-0 group-hover:opacity-100 transition-opacity`}>
        <span>{ctaLabel}</span>
        <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
      </div>
    </motion.button>
  );
}

function Stat({ label, value, color, darkMode }) {
  const muted = darkMode ? 'text-slate-500' : 'text-slate-400';
  return (
    <div className={`text-center py-2 rounded-lg ${darkMode ? 'bg-slate-900/40' : 'bg-slate-50/80'}`}>
      <div className={`am-numeric text-lg ${color}`}>{value}</div>
      <div className={`am-eyebrow text-[8px] mt-0.5 ${muted}`}>{label}</div>
    </div>
  );
}
